import React, { useState, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Progress,
  Spinner
} from 'reactstrap';
import { processBulkUpload, validateFile } from '../../services/CSVProcessorService';

interface BulkUploadModalProps {
  isOpen: boolean;
  toggle: () => void;
  onUploadComplete: (result: ProcessingResult) => void;
  onError: (error: string) => void;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

interface ProcessingResult {
  success: boolean;
  processedCount?: number;
  errorCount?: number;
  errors?: Array<{
    row: number | string;
    errors: string[];
  }>;
  error?: string;
  data?: any[];
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  toggle,
  onUploadComplete,
  onError
}) => {
  const [selectedTable, setSelectedTable] = useState('cliente');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [progress, setProgress] = useState(0);
  
  const fileInputRef = useRef<any>(null);

  // Available tables for bulk upload
  const availableTables = [
    { value: 'cliente', label: 'Cliente' }
  ];

  const handleTableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTable(e.target.value);
    setFileError('');
    setProcessingResult(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setFileError('');
    setProcessingResult(null);

    if (file) {
      const validation = validateFile(file) as ValidationResult;
      if (!validation.isValid) {
        setFileError(validation.errors.join(', '));
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setFileError('Por favor seleccione un archivo CSV');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setProcessingResult(null);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Read file content
      const fileContent = await readFileContent(selectedFile);
      
      // Process the CSV
      const result = await processBulkUpload(fileContent, selectedTable) as ProcessingResult;
      
      clearInterval(progressInterval);
      setProgress(100);
      
      setProcessingResult(result);
      
      if (result.success) {
        onUploadComplete(result);
      } else {
        onError(result.error || 'Error desconocido');
      }
      
    } catch (error) {
      console.error('Error during processing:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al procesar el archivo';
      setProcessingResult({
        success: false,
        error: errorMessage
      });
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => reject(new Error('Error al leer el archivo'));
      reader.readAsText(file);
    });
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileError('');
    setProcessingResult(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toggle();
  };

  const renderProcessingResult = () => {
    if (!processingResult) return null;

    if (processingResult.success) {
      return (
        <Alert color="success" className="mt-3">
          <h6>Procesamiento completado exitosamente</h6>
          <p>Registros procesados: {processingResult.processedCount}</p>
          {processingResult.errorCount && processingResult.errorCount > 0 && (
            <p>Errores encontrados: {processingResult.errorCount}</p>
          )}
        </Alert>
      );
    } else {
      return (
        <Alert color="danger" className="mt-3">
          <h6>Error en el procesamiento</h6>
          {processingResult.error && (
            <p>{processingResult.error}</p>
          )}
          {processingResult.errors && processingResult.errors.length > 0 && (
            <div>
              <p><strong>Errores de validación encontrados:</strong></p>
              <ul className="mb-0">
                {processingResult.errors.map((error, index) => (
                  <li key={index}>
                    {error.errors.map((err, errIndex) => (
                      <div key={errIndex}>{err}</div>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Alert>
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Carga Masiva de Parámetros
      </ModalHeader>
      
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label for="table-select">Seleccionar tabla *</Label>
            <Input
              type="select"
              id="table-select"
              name="table"
              value={selectedTable}
              onChange={handleTableChange}
              required
            >
              {availableTables.map(table => (
                <option key={table.value} value={table.value}>
                  {table.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="file-input">Seleccionar archivo CSV *</Label>
            <Input
              ref={fileInputRef}
              type="file"
              id="file-input"
              name="file"
              accept=".csv"
              onChange={handleFileChange}
              required
            />
            <div className="form-text">
              Tamaño máximo: 5MB. Formato requerido: CSV con encabezados
            </div>
          </FormGroup>

          {fileError && (
            <Alert color="danger" className="mt-2">
              {fileError}
            </Alert>
          )}

          {isProcessing && (
            <div className="mt-3">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" className="me-2" />
                <span>Procesando...</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {renderProcessingResult()}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isProcessing}>
            Cancelar
          </Button>
          <Button 
            color="primary" 
            type="submit" 
            disabled={!selectedFile || isProcessing || !!fileError}
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Procesando...
              </>
            ) : (
              'Procesar'
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BulkUploadModal;
